import { getCheckMonitor, type CheckMonitorQuery } from 'api/checkMonitor.api';
import type {
  CheckMonitorPeriod,
  CheckMonitorResponse,
} from 'types/checkMonitor';
import { getApiErrorMessage } from 'types';
import {
  browserTimeZone,
  dayOfMonth,
  localDate,
  moveReference,
  periodKey,
} from '../components/CheckMonitor/calendar';

export interface MonitorState {
  query: CheckMonitorQuery;
  data?: CheckMonitorResponse;
  loading: boolean;
  error?: string;
  following: boolean;
}

// One coordinator owns all request sources. Selection revisions reject stale success
// AND failure; a microtask coalesces visibility, device changes and timer events.
export class CheckMonitorSession {
  private listeners = new Set<() => void>();
  private state: MonitorState;
  private revision = 0;
  private preferredDay: number;
  private active?: AbortController;
  private pending = false;
  private scheduled = false;
  private started = false;
  private timer?: ReturnType<typeof setInterval>;

  constructor() {
    const timeZone = browserTimeZone();
    const anchor = timeZone ? localDate(new Date(), timeZone) : undefined;
    this.state = {
      query: { period: 'day', anchor, timeZone },
      following: true,
      loading: true,
    };
    this.preferredDay = anchor ? dayOfMonth(anchor) : 1;
  }

  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private update(patch: Partial<MonitorState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener());
  }
  private today() {
    const zone = this.state.query.timeZone;
    return zone ? localDate(new Date(), zone) : undefined;
  }
  private advanceCurrent() {
    const today = this.today();
    const { query, following } = this.state;
    if (!following || !today) return;
    if (
      !query.anchor ||
      periodKey(today, query.period) !== periodKey(query.anchor, query.period)
    ) {
      this.revision += 1;
      this.preferredDay = dayOfMonth(today);
      this.update({
        query: { ...query, anchor: today },
        data: undefined,
        error: undefined,
      });
    }
  }
  private schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    queueMicrotask(() => {
      this.scheduled = false;
      void this.pump();
    });
  }
  private async pump() {
    if (
      !this.started ||
      this.active ||
      !this.pending ||
      document.visibilityState === 'hidden'
    )
      return;
    this.advanceCurrent();
    this.pending = false;
    const revision = this.revision;
    const query = { ...this.state.query };
    const controller = new AbortController();
    this.active = controller;
    this.update({ loading: true });
    try {
      const data = await getCheckMonitor(query, controller.signal);
      if (
        !this.started ||
        controller.signal.aborted ||
        revision !== this.revision
      )
        return;
      // Adopt the backend's timezone and initial reference, including detection fallback.
      this.update({
        data,
        query: {
          period: data.period,
          anchor: data.anchor,
          timeZone: data.timeZone,
        },
        error: undefined,
      });
      if (!query.anchor) this.preferredDay = dayOfMonth(data.anchor);
    } catch (error) {
      if (
        !this.started ||
        controller.signal.aborted ||
        revision !== this.revision
      )
        return;
      this.update({
        error: getApiErrorMessage(
          error,
          'No fue posible consultar las checadas. Intenta nuevamente.'
        ),
      });
    } finally {
      if (this.active === controller) this.active = undefined;
      if (this.started && revision === this.revision)
        this.update({ loading: false });
      if (this.pending) this.schedule();
    }
  }
  refresh = () => {
    this.pending = true;
    this.schedule();
  };
  private onVisibility = () => {
    if (document.visibilityState !== 'hidden') this.refresh();
  };
  start = () => {
    this.started = true;
    this.refresh();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.timer = setInterval(() => {
      if (
        document.visibilityState === 'hidden' ||
        !this.state.following ||
        this.active
      )
        return;
      this.refresh();
    }, 60000);
    return () => {
      this.started = false;
      this.revision += 1;
      this.pending = false;
      this.active?.abort();
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
    };
  };
  private select(query: CheckMonitorQuery, following: boolean) {
    this.revision += 1;
    this.active?.abort();
    this.update({
      query,
      following,
      data: undefined,
      error: undefined,
      loading: true,
    });
    this.refresh();
  }
  setPeriod = (period: CheckMonitorPeriod) => {
    if (period === this.state.query.period) return;
    const today = this.today();
    const anchor = this.state.query.anchor;
    this.preferredDay = anchor ? dayOfMonth(anchor) : 1;
    this.select(
      { ...this.state.query, period },
      !anchor ||
        !today ||
        periodKey(anchor, period) === periodKey(today, period)
    );
  };
  navigate = (direction: -1 | 1) => {
    const { query } = this.state;
    const today = this.today();
    if (!query.anchor || !today) return;
    let anchor = moveReference(
      query.anchor,
      query.period,
      direction,
      this.preferredDay
    );
    if (periodKey(anchor, query.period) > periodKey(today, query.period))
      return;
    if (anchor > today) anchor = today;
    const following =
      periodKey(anchor, query.period) === periodKey(today, query.period);
    this.select({ ...query, anchor }, following);
  };
}
